import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { TrendingDown, TrendingUp, Globe2, Package } from 'lucide-react';
interface SlideMarketSizeProps {
  onContinue: () => void;
}
export function SlideMarketSize({
  onContinue
}: SlideMarketSizeProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [400, 1000, 1800, 2600, 3400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  const targetPct = 76;
  const radius = 78;
  const circumference = 2 * Math.PI * radius;
  const pctSpring = useSpring(0, {
    stiffness: 60,
    damping: 18
  });
  const dashOffset = useTransform(pctSpring, latest => circumference - circumference * latest / 100);
  const displayPct = useTransform(pctSpring, latest => `${Math.round(latest)}%`);
  useEffect(() => {
    if (stage >= 2) pctSpring.set(targetPct);
  }, [stage]);
  const facts = [{
    value: '25+',
    label: 'Promoted brands',
    icon: <Package className="w-5 h-5" data-id="element-909" data-mp="2r214g57gjww" />
  }, {
    value: '70+',
    label: 'Active markets',
    icon: <Globe2 className="w-5 h-5" data-id="element-910" data-mp="3pxkwc7c7hsmk" />
  }, {
    value: '31',
    label: 'Affiliate markets',
    icon: <Globe2 className="w-5 h-5" data-id="element-911" data-mp="3tywj853p3zr2" />
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-912" data-mp="3txd1ea2e5gn6">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3" data-id="element-913" data-mp="3g6gm1kxh6vy2">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-914" data-mp="36bew4mvdlpuf">
          LEO Pharma Today
        </p>
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-915" data-mp="3b6c03fdst90g">
          The established base is the engine
        </h2>
        <p className="text-lg text-white/60" data-id="element-916" data-mp="328i8ghqzb3jm">
          DKK 13.5bn revenue in 2025 — and most of it comes from brands past
          exclusivity.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6" data-id="element-917" data-mp="1311t7a1y03e9">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-918" data-mp="12z8nbwz5ln7b">
          {/* Donut */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          scale: stage >= 1 ? 1 : 0.9
        }} transition={{
          duration: 0.7,
          type: 'spring'
        }} className="lg:col-span-5 flex items-center justify-center" data-id="element-919" data-mp="u0dese8n010u">
            <div className="relative" data-id="element-920" data-mp="1crea8sajtefn">
              <svg width="230" height="230" viewBox="0 0 200 200" data-id="element-921" data-mp="1cqcx8i9z2hva">
                <circle cx="100" cy="100" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="24" data-id="element-922" data-mp="6b2l1gppf6w7" />
                <motion.circle cx="100" cy="100" r={radius} fill="none" stroke="#7c3aed" strokeWidth="24" strokeLinecap="round" strokeDasharray={circumference} style={{
                strokeDashoffset: dashOffset
              }} transform="rotate(-90 100 100)" data-id="element-923" data-mp="2d0cppdamg3d6" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-924" data-mp="30nkodcdjoe68">
                <motion.div className="text-5xl font-serif text-teal-400" data-id="element-925" data-mp="35p1q1a3us4nv">
                  {displayPct}
                </motion.div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mt-1 max-w-[120px] leading-tight" data-id="element-926" data-mp="39mzh6ih6p0tw">
                  Revenue from
                  <br data-id="element-927" data-mp="1fc2jsn7psvgs" />
                  legacy brands
                </div>
              </div>
            </div>
          </motion.div>

          {/* Split */}
          <div className="lg:col-span-7 space-y-4" data-id="element-928" data-mp="fzsui5qd0gyw">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: stage >= 2 ? 1 : 0,
            x: stage >= 2 ? 0 : 20
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-929" data-mp="fz9dqscg3ymg">
              <div className="flex items-start gap-4" data-id="element-930" data-mp="3sh7ly0dnzwni">
                <div className="w-11 h-11 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center flex-shrink-0" data-id="element-931" data-mp="3siimlrbtcoae">
                  <TrendingDown className="w-5 h-5" data-id="element-932" data-mp="69puvmoqg4rl" />
                </div>
                <div data-id="element-933" data-mp="68erhnz78z10">
                  <div className="flex flex-wrap items-baseline gap-x-3 mb-1" data-id="element-934" data-mp="3epjb00f5je84">
                    <h3 className="text-2xl font-serif text-white" data-id="element-935" data-mp="3engc0qw0uar9">
                      DKK 10.2bn
                    </h3>
                    <span className="text-sm font-semibold text-white/50" data-id="element-936" data-mp="1p8dxc8mbub6h">
                      +2% CER
                    </span>
                  </div>
                  <p className="text-white/70 leading-relaxed text-sm" data-id="element-937" data-mp="1u0oxnckzbyzk">
                    <span className="text-white font-medium" data-id="element-938" data-mp="1g8im63srug6g">
                      Established brands (DKK 8.0bn) + Critical Care (DKK 2.3bn)
                    </span>{' '}
                    — 20+ mature dermatology brands and the thrombosis
                    portfolio, most of them off patent. Stable, cash-generative,
                    barely promoted.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: stage >= 3 ? 1 : 0,
            x: stage >= 3 ? 0 : 20
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6" data-id="element-939" data-mp="1z0jqhlobeyn9">
              <div className="flex items-start gap-4" data-id="element-940" data-mp="2u7kbsheg98d2">
                <div className="w-11 h-11 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center flex-shrink-0" data-id="element-941" data-mp="2u9evjsdziufk">
                  <TrendingUp className="w-5 h-5" data-id="element-942" data-mp="2kgzeqyefcbw9" />
                </div>
                <div data-id="element-943" data-mp="2kf4wxsyg8zwe">
                  <div className="flex flex-wrap items-baseline gap-x-3 mb-1" data-id="element-944" data-mp="2ggfj5z9i1gc8">
                    <h3 className="text-2xl font-serif text-white" data-id="element-945" data-mp="2bgt5joprdy2w">
                      DKK 3.0bn
                    </h3>
                    <span className="text-sm font-semibold text-purple-500" data-id="element-946" data-mp="26h6qraua89rz">
                      +48% CER
                    </span>
                  </div>
                  <p className="text-white/70 leading-relaxed text-sm" data-id="element-947" data-mp="21kpws00ej16z">
                    <span className="text-white font-medium" data-id="element-948" data-mp="1ximfgur1x2ds">
                      Strategic brands
                    </span>{' '}
                    — Adtralza®/Adbry®, Anzupgo®, Spevigo®. 22% of revenue, and
                    where nearly all commercial investment is concentrated.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Facts */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="grid grid-cols-3 gap-4" data-id="element-949" data-mp="1ikaa7nbdj6qf">
          {facts.map(fact => <div key={fact.label} className="bg-navy-700/40 border border-navy-600 rounded-2xl px-5 py-4 flex items-center gap-3" data-id="element-950" data-mp="uqii4v7llark">
              <div className="w-9 h-9 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center shrink-0" data-id="element-951" data-mp="13nkjmxjlaapi">
                {fact.icon}
              </div>
              <div data-id="element-952" data-mp="2f52f79r0o40y">
                <p className="text-xl font-serif text-white leading-none" data-id="element-953" data-mp="2f3hl1l8m6n3m">
                  {fact.value}
                </p>
                <p className="text-white/50 text-xs uppercase tracking-wider font-semibold mt-1" data-id="element-954" data-mp="2k5gpkyg3bq25">
                  {fact.label}
                </p>
              </div>
            </div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        y: stage >= 5 ? 0 : 10
      }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl px-7 py-5" data-id="element-955" data-mp="24j3vc67xjwti">
          <p className="text-white/90 leading-relaxed" data-id="element-956" data-mp="29i71jtznc932">
            LEO's legacy portfolio funds the next generation of innovation — yet
            it is the part of the business that can least afford traditional
            agency campaigns.{' '}
            <span className="text-teal-400 font-semibold" data-id="element-957" data-mp="2jhpj63s33iry">
              25+ brands across 70+ markets need scale and relevance at the same
              time.
            </span>
          </p>
        </motion.div>
      </div>
    </div>;
}